import { css } from "@emotion/css";
import { InlineMessage, RadioButtonNew, RadioGroupNew } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { TemplateBanner } from "~/areas/platformHub/TemplateBanner";
import { UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Templates/TemplateVersioningSection";
interface VersionTypeSelectionProps {
    latestStableVersion: string | null | undefined;
    latestPreReleaseVersion: string | null | undefined;
    selectedUsageConstraint: UsageConstraint;
    onUsageConstraintChanged: (value: UsageConstraint) => void;
    showHeading?: boolean;
}
function VersionTypeSelection({ latestStableVersion, latestPreReleaseVersion, selectedUsageConstraint, onUsageConstraintChanged, showHeading = true }: VersionTypeSelectionProps) {
    if (!latestPreReleaseVersion) {
        // If there's no pre-release version, don't show this selector
        return null;
    }
    return (<div className={styles.selectionContainer}>
            {showHeading && <div className={styles.selectionHeading}>Select preferred version type</div>}

            <RadioGroupNew label={"Version type selection"} value={selectedUsageConstraint} onChange={onUsageConstraintChanged} hideLabel={true}>
                <RadioButtonNew value={UsageConstraint.Stable} label={"Stable"} name={"stable-button"} disabled={!latestStableVersion}/>
                <div className={styles.optionDescription}>
                    Intended for production use. Recommended for most users.
                    {latestStableVersion && <> Current version: {latestStableVersion}.</>}
                </div>
                <RadioButtonNew value={UsageConstraint.PreRelease} label={"Pre-release"} name={"prerelease-button"}/>
                <div className={styles.optionDescription}>Current version: {latestPreReleaseVersion}. Pre-releases are intended for testing purposes, not regular deployments. Proceed with caution.</div>
            </RadioGroupNew>
        </div>);
}
interface UpdateBehaviourSelectionProps {
    selectedUpdateBehaviour: UpdateBehaviour;
    onUpdateBehaviourChanged: (value: UpdateBehaviour) => void;
    showHeading?: boolean;
}
function UpdateBehaviourSelection({ selectedUpdateBehaviour, onUpdateBehaviourChanged, showHeading = true }: UpdateBehaviourSelectionProps) {
    return (<div className={styles.selectionContainer}>
            {showHeading && <div className={styles.selectionHeading}>Select how you'd like the template to update</div>}

            <RadioGroupNew label={"Update behavior selection"} value={selectedUpdateBehaviour} onChange={onUpdateBehaviourChanged} hideLabel={true}>
                <RadioButtonNew label="Accept minor changes" value={UpdateBehaviour.Minor} name={"minor-button"}/>
                <div className={styles.optionDescription}>Automatically update the template whenever a patch or minor version is published. Major versions will require a manual update.</div>
                <RadioButtonNew label="Accept patches" value={UpdateBehaviour.Patch} name={"patch-button"}/>
                <div className={styles.optionDescription}>Automatically update the template whenever a patch is published. Major and minor versions will require a manual update.</div>
            </RadioGroupNew>

            <InlineMessage type={"information"} description={"Major versions will always require a manual update."}/>
        </div>);
}
export interface TemplateVersionSummary {
    LatestStableVersion: string | null;
    LatestPreReleaseVersion: string | null;
}
interface VersionSelectionContentProps {
    iconLink?: string;
    heading: string;
    versionSummary: TemplateVersionSummary;
    selectedUsageConstraint: UsageConstraint;
    onUsageConstraintChanged: (value: UsageConstraint) => void;
    /** When true, omit the TemplateBanner (for use in pages with custom template display) */
    hideBanner?: boolean;
}
export function VersionSelectionContent({ iconLink, heading, versionSummary, selectedUsageConstraint, onUsageConstraintChanged, hideBanner = false }: VersionSelectionContentProps) {
    const selection = (<VersionTypeSelection latestStableVersion={versionSummary.LatestStableVersion} latestPreReleaseVersion={versionSummary.LatestPreReleaseVersion} selectedUsageConstraint={selectedUsageConstraint} onUsageConstraintChanged={onUsageConstraintChanged}/>);
    if (hideBanner)
        return selection;
    return (<div className={styles.stepContainer}>
            <TemplateBanner iconLink={iconLink} heading={heading} subHeading={getVersionInfo(versionSummary)}/>
            {selection}
        </div>);
}
interface UpdateBehaviourContentProps {
    iconLink?: string;
    heading: string;
    versionSummary: TemplateVersionSummary;
    selectedUpdateBehaviour: UpdateBehaviour;
    onUpdateBehaviourChanged: (value: UpdateBehaviour) => void;
    /** When true, omit the TemplateBanner (for use in pages with custom template display) */
    hideBanner?: boolean;
}
export function UpdateBehaviourContent({ iconLink, heading, versionSummary, selectedUpdateBehaviour, onUpdateBehaviourChanged, hideBanner = false }: UpdateBehaviourContentProps) {
    const selection = <UpdateBehaviourSelection selectedUpdateBehaviour={selectedUpdateBehaviour} onUpdateBehaviourChanged={onUpdateBehaviourChanged}/>;
    if (hideBanner)
        return selection;
    return (<div className={styles.stepContainer}>
            <TemplateBanner iconLink={iconLink} heading={heading} subHeading={getVersionInfo(versionSummary)}/>
            {selection}
        </div>);
}
function getVersionInfo(versionSummary: TemplateVersionSummary) {
    return (<>
            {versionSummary.LatestStableVersion && versionSummary.LatestPreReleaseVersion ? (<>
                    v{versionSummary.LatestStableVersion}, v{versionSummary.LatestPreReleaseVersion}
                </>) : versionSummary.LatestStableVersion ? (<>v{versionSummary.LatestStableVersion}</>) : versionSummary.LatestPreReleaseVersion ? (<>v{versionSummary.LatestPreReleaseVersion}</>) : null}
        </>);
}
const styles = {
    selectionContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
        marginBottom: space[24],
    }),
    selectionHeading: css({
        font: text.body.bold.large,
        color: themeTokens.color.text.primary,
        margin: 0,
    }),
    optionDescription: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
        marginLeft: "1.75rem",
        marginBottom: space[16],
        lineHeight: 1.4,
    }),
    stepContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[32],
    }),
};
